import { nmlSuzhouDateInputValue } from "../../utils/nmlSuzhouCatalog";

export const BOARD_ACCENT = {
  SUZHOU: "#0f766e",
  TURKEY: "#c2410c",
  GUANGZHOU: "#4f46e5",
};

export const SECTION_ACCENT = {
  order: "#4338ca",
  line: "#64748b",
  calendar: "#0284c7",
  fabric: "#d97706",
  production: "#0f766e",
  sizes: "#7c3aed",
};

export const NML_PINNED_DATA_FIELDS = ["nmlPo", "styleReference"];

export function nmlShowsNotesAndChat(clientView) {
  return !clientView;
}

export function nmlPinnedLeftFields({
  clientView = false,
  canEdit = false,
} = {}) {
  const left = ["look"];
  if (nmlShowsNotesAndChat(clientView)) left.push("chat", "notes");
  if (canEdit) left.push("cerrar", "nmlPo");
  left.push("styleReference");
  return left;
}

const SEARCH_FIELDS = [
  "nmlPo",
  "clientPo",
  "styleReference",
  "styleDescription",
  "nmlNo",
  "colorName",
  "merchandiser",
  "factory",
  "productName",
  "garmentDescription",
];

const NAMED_COLORS = {
  black: "#1a1a1a",
  negro: "#1a1a1a",
  blanco: "#f4f4f4",
  white: "#f4f4f4",
  navy: "#1e3a5f",
  azul: "#2563eb",
  blue: "#2563eb",
  celeste: "#7dd3fc",
  aqua: "#22d3ee",
  agua: "#22d3ee",
  red: "#b91c1c",
  rojo: "#b91c1c",
  green: "#047857",
  verde: "#047857",
  grey: "#6b7280",
  gray: "#6b7280",
  gris: "#6b7280",
  beige: "#d6c4a8",
  camel: "#c19a6b",
  brown: "#7c4a1e",
  marron: "#7c4a1e",
  pink: "#db2777",
  rosa: "#db2777",
  orange: "#ea580c",
  naranja: "#ea580c",
  yellow: "#ca8a04",
  amarillo: "#ca8a04",
  khaki: "#b8a06a",
  ivory: "#fffff0",
  cream: "#f5f0e1",
  crema: "#f5f0e1",
  gold: "#c9a227",
  oro: "#c9a227",
  silver: "#c0c0c0",
  plata: "#c0c0c0",
  purple: "#7c3aed",
  morado: "#7c3aed",
  wine: "#722f37",
  vino: "#722f37",
};

export function filterNmlSuzhouRows(rows = [], query = "") {
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) return rows;
  return rows.filter((row) =>
    SEARCH_FIELDS.some((key) =>
      String(row?.[key] || "")
        .toLowerCase()
        .includes(q),
    ),
  );
}

export function buildNmlColumnGroupingModel(columns = []) {
  const groups = [];
  const groupMap = new Map();

  for (const col of columns) {
    if (!col?.key || NML_PINNED_DATA_FIELDS.includes(col.key)) continue;
    if (!col.groupId) continue;
    if (!groupMap.has(col.groupId)) {
      const group = {
        groupId: col.groupId,
        headerName: col.groupLabel || col.groupId,
        headerClassName: col.section
          ? `nml-task-group nml-section-${col.section}`
          : "nml-task-group",
        children: [],
      };
      groupMap.set(col.groupId, group);
      groups.push(group);
    }
    groupMap.get(col.groupId).children.push({ field: col.key });
  }

  return groups;
}

export function nmlFieldI18nId(col) {
  if (col?.taskKey) return col.taskKey;
  const key = String(col?.key || "");
  const task = key.match(/^task\.(.+?)(?:\.(expected|real))?$/);
  if (task) return task[1];
  return key || null;
}

export function nmlHeaderI18nKey(col) {
  if (col?.slot === "expected") return "NmlSuzhou.slotExpected";
  if (col?.slot === "real") return "NmlSuzhou.slotReal";
  const field = nmlFieldI18nId(col);
  return field ? `NmlSuzhou.cols.${field}` : null;
}

export function nmlGroupI18nKey(groupId) {
  if (groupId === "sizeBreakDown") return "NmlSuzhou.sections.sizes";
  const m = String(groupId || "").match(/^task\.(.+)$/);
  if (m) return `NmlSuzhou.cols.${m[1]}`;
  return null;
}

export function withSectionStarts(columns = []) {
  let previous = null;
  return columns.map((col) => {
    const section = NML_PINNED_DATA_FIELDS.includes(col.key)
      ? null
      : col.section || null;
    const sectionStart = Boolean(section && section !== previous);
    if (section) previous = section;
    return { ...col, sectionStart };
  });
}

export function shouldChipStatus(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  if (text.length > 16) return false;
  return text.split(/\s+/).length <= 2;
}

export function isNmlStatusField(key) {
  return (
    key === "fabricBulkStatus" || key === "packingStatus" || key === "transport"
  );
}

export function nmlCellKind(fieldKey) {
  if (fieldKey === "nmlPo" || fieldKey === "clientPo" || fieldKey === "nmlNo") {
    return "code";
  }
  if (fieldKey === "styleReference") return "style";
  if (fieldKey === "merchandiser" || fieldKey === "patternMaker")
    return "person";
  if (fieldKey === "factory") return "factory";
  if (fieldKey === "transport") return "transport";
  if (fieldKey === "colorName") return "color";
  if (
    fieldKey === "qty" ||
    fieldKey === "qtyTotal" ||
    /^qty(Xs|S|M|L|Xl|Xxl)$/.test(fieldKey)
  ) {
    return "qty";
  }
  if (fieldKey === "fabricBulkStatus" || fieldKey === "packingStatus") {
    return "status";
  }
  if (
    fieldKey === "comments" ||
    fieldKey === "fittingComments" ||
    fieldKey === "smsFabricComments" ||
    fieldKey === "strikeOff"
  ) {
    return "note";
  }
  return "text";
}

export function nmlInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export function nmlTransportKind(value) {
  const text = String(value || "").toLowerCase();
  if (!text) return null;
  if (/\bair\b|avi[oó]n|a[eé]reo/.test(text)) return "air";
  if (/\bsea\b|ocean|barco|fob/.test(text)) return "sea";
  if (/\btruck\b|land|cami[oó]n/.test(text)) return "truck";
  return "other";
}

const STATUS_TOKEN_RULES = [
  { re: /approv|ok\b|ready|complete/i, token: "approved", tone: "success" },
  { re: /in\s*house|received/i, token: "inHouse", tone: "success" },
  { re: /mock-?up/i, token: "mockUp", tone: "info" },
  { re: /\bsent\b|\bsend\b/i, token: "sent", tone: "info" },
  { re: /pend|wait|tbd|not yet/i, token: "pending", tone: "warning" },
  { re: /delay|late|hold/i, token: "delay", tone: "error" },
  { re: /cancel|reject|fail/i, token: "blocked", tone: "error" },
];

export function extractNmlStatusTokens(value) {
  const text = String(value || "").trim();
  if (!text) return [];
  const found = [];
  const seen = new Set();
  for (const rule of STATUS_TOKEN_RULES) {
    if (!rule.re.test(text) || seen.has(rule.token)) continue;
    seen.add(rule.token);
    found.push({ token: rule.token, tone: rule.tone });
  }
  return found;
}

export function nmlFirstLine(value) {
  return (
    String(value || "")
      .replace(/\r/g, "")
      .split("\n")
      .map((line) => line.trim())
      .find(Boolean) || ""
  );
}

export function nmlStatusTone(value) {
  const text = String(value || "").toLowerCase();
  if (!text) return "default";
  if (
    /(ok|done|ready|in.?house|received|approved|complete|yes|air|sea)/.test(
      text,
    )
  ) {
    return "success";
  }
  if (/(pend|wait|delay|hold|tbd|not yet)/.test(text)) return "warning";
  if (/(cancel|reject|fail|late|\bno\b)/.test(text)) return "error";
  return "default";
}

export function nmlColorSwatch(name) {
  const raw = String(name || "")
    .trim()
    .toLowerCase();
  if (!raw) return null;
  for (const [key, hex] of Object.entries(NAMED_COLORS)) {
    if (raw.includes(key)) return hex;
  }
  return null;
}

export function isNmlTaskLate(row, col) {
  if (col?.slot !== "real") return false;
  const task = row?.tasks?.[col.taskKey] || {};
  const expected = nmlSuzhouDateInputValue(task.expected);
  const real = nmlSuzhouDateInputValue(task.real);
  return Boolean(expected && real && real > expected);
}

export function nmlRowIsLate(row) {
  const tasks = row?.tasks || {};
  return Object.keys(tasks).some((taskKey) =>
    isNmlTaskLate(row, { slot: "real", taskKey }),
  );
}

export function summarizeNmlBoard(rows = []) {
  let closed = 0;
  let late = 0;
  for (const row of rows) {
    if (row?.isClosed) closed += 1;
    if (nmlRowIsLate(row)) late += 1;
  }
  return {
    total: rows.length,
    closed,
    late,
    open: Math.max(0, rows.length - closed),
  };
}

export function nmlColumnWidth(col) {
  if (col.key === "nmlPo" || col.key === "nmlNo") return 124;
  if (col.key === "clientPo") return 118;
  if (col.key === "styleReference") return 158;
  if (col.key === "styleDescription" || col.key === "garmentDescription") {
    return 176;
  }
  if (col.key === "comments" || col.key === "fittingComments") return 188;
  if (col.key === "fabricBulkStatus") return 176;
  if (col.key === "colorName") return 140;
  if (col.key === "composition") return 132;
  if (col.key === "merchandiser") return 132;
  if (col.key === "factory") return 148;
  if (col.key === "transport") return 136;
  if (col.groupId === "sizeBreakDown") return 58;
  if (col.key === "qty" || col.key === "qtyTotal") return 80;
  if (col.slot) return 90;
  if (col.kind === "date") return 128;
  if (col.kind === "number") return 76;
  return 128;
}

export function nmlHeaderClassName(col) {
  const bits = [];
  if (col.slot === "expected") bits.push("nml-h-expected");
  else if (col.slot === "real") bits.push("nml-h-real");
  else if (col.section) bits.push(`nml-h-${col.section}`);
  if (col.sectionStart) bits.push("nml-section-start");
  return bits.join(" ");
}

export function nmlCellClassName(col, row) {
  const bits = [];
  if (isNmlTaskLate(row, col)) bits.push("nml-c-late");
  else if (col.slot === "expected") bits.push("nml-c-expected");
  else if (col.slot === "real") bits.push("nml-c-real");
  if (col.sectionStart) bits.push("nml-section-start");
  return bits.join(" ");
}

export function formatNmlNumber(value) {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  const n = Number(raw.replace(/[^\d.-]/g, ""));
  if (!Number.isFinite(n) || /[^\d.,\s$-]/.test(raw)) return raw;
  return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(n);
}
